import Axios from 'axios';
import { getZenLan } from '@/config/const';

// Zendesk 配置文件
export const ZENDESK_CONFIG = {
  // Zendesk Widget Key，优先从环境变量读取（由上级 web_configs 注入）
  WIDGET_KEY: process.env.REACT_APP_ZENDESK_WIDGET_KEY || 'xxxx',

  // 可选：仅作文档/与后台对照；Messaging Web Widget 不支持 zE('messenger','updateSettings')，主题与文案请在 Zendesk 管理后台配置。
  CUSTOM_CONFIG: {
    // 聊天窗口颜色主题
    color: {
      theme: '#007bff'
    },
    // 聊天窗口位置
    position: {
      horizontal: 'right',
      vertical: 'bottom'
    },
    // 聊天窗口偏移
    offset: {
      horizontal: '20px',
      vertical: '20px'
    },
    // 聊天标题
    chat: {
      title: {
        '*': '在线客服'
      }
    }
  }
};

let zendeskAuthPromise: Promise<void> | null = null;
let zendeskInitPromise: Promise<void> | null = null;
let zendeskScriptPromise: Promise<void> | null = null;
let lastZendeskUid: string | null = null;
let zendeskLoginReady = false;
let initVersion = 0;

const isValidZendeskWidgetKey = (key: string) => Boolean(key && key !== 'xxxx');

const ensureZendeskScript = (): Promise<void> => {
  const { WIDGET_KEY } = ZENDESK_CONFIG;
  if (!isValidZendeskWidgetKey(WIDGET_KEY)) {
    return Promise.resolve();
  }

  if (zendeskScriptPromise) {
    return zendeskScriptPromise;
  }

  const existing = document.getElementById('ze-snippet') as HTMLScriptElement | null;
  if (existing?.getAttribute('data-loaded') === 'true') {
    zendeskScriptPromise = Promise.resolve();
    return zendeskScriptPromise;
  }

  zendeskScriptPromise = new Promise((resolve, reject) => {
    const handleLoad = () => resolve();
    const handleError = () => reject(new Error('Failed to load Zendesk widget script'));

    if (existing) {
      existing.addEventListener('load', handleLoad, { once: true });
      existing.addEventListener('error', handleError, { once: true });
      return;
    }

    const script = document.createElement('script');
    script.id = 'ze-snippet';
    script.src = `https://static.zdassets.com/ekr/snippet.js?key=${WIDGET_KEY}`;
    script.async = true;
    script.onload = () => {
      script.setAttribute('data-loaded', 'true');
      handleLoad();
    };
    script.onerror = handleError;
    document.body.appendChild(script);
  });

  return zendeskScriptPromise;
};

const ZENDESK_TOKEN_API = '/lv/need/t/getKfToken';
const ZENDESK_WIDGET_LOCALE_MAP: Record<string, string> = {
  vi: 'vi',
};

const getZendeskWidgetLocale = (lang?: string) => {
  if (lang && ZENDESK_WIDGET_LOCALE_MAP[lang]) {
    return ZENDESK_WIDGET_LOCALE_MAP[lang];
  }
  return getZenLan((lang || 'en') as any);
};

export const setZendeskLocale = (lang?: string) => {
  if (!window.zE) return;
  window.zE('messenger:set', 'locale', getZendeskWidgetLocale(lang));
};

const pickJwtString = (value: any): string => {
  if (!value) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'object') {
    const direct = value.token || value.jwt || value.kfToken || value.accessToken || value.data || '';
    if (typeof direct === 'string') return direct;
    if (typeof value.data === 'string') return value.data;
    if (value.data && typeof value.data === 'object') {
      const nested = value.data.token || value.data.jwt || value.data.kfToken || value.data.accessToken;
      if (typeof nested === 'string') return nested;
    }
  }
  return '';
};

const resolveJwtFromResponse = (res: any): string => {
  return pickJwtString(res);
};

const fetchZendeskJwt = async (uid: string) => {
  const res: any = await Axios.get(ZENDESK_TOKEN_API, {
    params: {
      userId: uid || '',
    },
  });
  const jwt = resolveJwtFromResponse(res);
  if (!jwt) {
    throw new Error('Zendesk JWT is empty');
  }
  return jwt;
};

const inspectJwtPayload = (jwt: string) => {
  try {
    const payload = JSON.parse(atob(jwt.split('.')[1] || ''));
    const hasExternalId = Boolean(payload?.external_id);
    const hasScopeUser = payload?.scope === 'user';
    const hasExp = Boolean(payload?.exp);
    if (!hasExternalId || !hasScopeUser || !hasExp) {
      console.warn('Zendesk JWT claims may be invalid:', {
        hasExternalId,
        hasScopeUser,
        hasExp,
      });
    }
  } catch (e) {
    console.warn('Unable to parse Zendesk JWT payload');
  }
};

const applyConversationMetadata = (uid: string) => {
  const uidTag = `uid_${uid}`;
  const uidFieldId = process.env.REACT_APP_ZENDESK_UID_FIELD_ID;
  const applyTags = () => {
    window.zE('messenger:set', 'conversationTags', [uidTag]);
  };
  if (uidFieldId) {
    window.zE('messenger:set', 'conversationFields', [{ id: String(uidFieldId), value: uid }], applyTags);
    return;
  }
  applyTags();
};

const loginZendeskUser = async (uid: string) => {
  let jwt = '';
  try {
    jwt = await fetchZendeskJwt(uid);
    inspectJwtPayload(jwt);
  } catch (error) {
    console.error('Fetch Zendesk JWT failed:', error);
    zendeskLoginReady = false;
    return;
  }

  return new Promise<void>((resolve) => {
    window.zE(
      'messenger',
      'loginUser',
      (jwtCallback: (token: string) => void) => {
        jwtCallback(jwt);
      },
      (error: any) => {
        if (error) {
          console.error('Zendesk loginUser failed:', error);
          zendeskLoginReady = false;
        } else {
          zendeskLoginReady = true;
          applyConversationMetadata(uid);
        }
        resolve();
      },
    );
  });
};

const logoutZendeskUser = () =>
  new Promise<void>((resolve) => {
    window.zE('messenger', 'logoutUser');
    zendeskLoginReady = false;
    resolve();
  });

// Zendesk Widget 初始化函数
export const initZendeskWidget = (userInfo?: any, lang?: string) => {
  if (!isValidZendeskWidgetKey(ZENDESK_CONFIG.WIDGET_KEY)) {
    return;
  }

  // 初始化 zE 函数（脚本加载前先入队调用）
  window.zE = window.zE || function (...args: any[]) {
    (window.zE.q = window.zE.q || []).push(args);
  };
  const currentInitVersion = ++initVersion;

  const runInit = () => {
    setZendeskLocale(lang);

    // 门闩：初始化期间隐藏官方 launcher，避免用户先点再初始化
    window.zE('messenger', 'hide');

    // 用户认证（Messaging 需使用 loginUser + JWT）
    if (userInfo?.id) {
      const uid = String(userInfo.id);
      if (lastZendeskUid !== uid || !zendeskLoginReady) {
        zendeskAuthPromise = loginZendeskUser(uid);
        lastZendeskUid = uid;
      } else {
        // 同一用户重复初始化时，继续确保 metadata 是最新的
        applyConversationMetadata(uid);
        zendeskAuthPromise = Promise.resolve();
      }
    } else if (lastZendeskUid) {
      zendeskAuthPromise = logoutZendeskUser();
      lastZendeskUid = null;
    } else {
      zendeskLoginReady = false;
      zendeskAuthPromise = Promise.resolve();
    }

    const authTask = zendeskAuthPromise || Promise.resolve();
    zendeskInitPromise = authTask.finally(() => {
      // 仅最新一次初始化可以放开 launcher，避免并发初始化导致状态覆盖
      if (currentInitVersion === initVersion) {
        window.zE('messenger', 'show');
      }
    });
  };

  ensureZendeskScript()
    .then(runInit)
    .catch(error => {
      console.error('Zendesk widget script failed to load:', error);
    });
};

// 打开聊天窗口
export const openZendeskChat = () => {
  if (!isValidZendeskWidgetKey(ZENDESK_CONFIG.WIDGET_KEY)) {
    return;
  }

  const doOpen = () => {
    window.zE('messenger', 'show');
    window.zE('messenger', 'open');
  };

  ensureZendeskScript()
    .then(() => {
      const waitTask = zendeskInitPromise || zendeskAuthPromise;
      if (waitTask) {
        waitTask.finally(doOpen);
        return;
      }
      doOpen();
    })
    .catch(error => {
      console.error('Zendesk widget script failed to load:', error);
    });
};

// 关闭聊天窗口
export const closeZendeskChat = () => {
  if (window.zE) {
    window.zE('messenger', 'hide');
    window.zE('messenger', 'close');
  }
};

// 切换聊天窗口状态
export const toggleZendeskChat = () => {
  if (window.zE) {
    window.zE('messenger', 'toggle');
  }
};

declare global {
  interface Window {
    zE: any;
  }
}
